import * as STD from '@emersya/mpa-std';
import type {Basket as TBasket} from './Basket';
import type TPricing from './Pricing';
import type TPart from './Part';
import type TReference from './Reference';
import type TPrice from './Price';

class Product {
	readonly basket: TBasket;
	readonly id: string;
	readonly viewerCode: string;
	readonly title: string;
	readonly parts = <TPart[]>[];
	reference: TReference | null = null;
	sku: TReference | null = null;
	gtin: TReference | null = null;
	quantity: number;
	metaData: STD.object.JSONComplexRecursive | null = null;
	thumbnailsURLs: string[] | null = null;
	configurationCode: string | null = null;
	configurationReplayURL: string | null = null;
	configuratorReplayURL: string | null = null;
	PDFURL: string | null = null;
	readonly pricing: TPricing.TPricing | null = null;
	unitPrice = window.BasketNS.Price.ofZero(); // auto-computed
	totalPrice = window.BasketNS.Price.ofZero(); // auto-computed
	isInitialized = false;

	setReference(reference?: string | TReference | null) {
		if (reference) {
			if (typeof reference === 'string') {
				this.reference = new window.BasketNS.Reference(reference);
			} else {
				this.reference = reference;
			}
		} else {
			this.reference = null;
		}
	}

	setSKU(sku?: string | TReference | null) {
		if (sku) {
			if (typeof sku === 'string') {
				this.sku = new window.BasketNS.Reference(sku);
			} else {
				this.sku = sku;
			}
		} else {
			this.sku = null;
		}
	}

	setGTIN(gtin?: string | TReference | null) {
		if (gtin) {
			if (typeof gtin === 'string') {
				this.gtin = new window.BasketNS.Reference(gtin);
			} else {
				this.gtin = gtin;
			}
		} else {
			this.gtin = null;
		}
	}

	async setThumbnailsURLs(thumbnailsURLs: string[] | null) {
		this.thumbnailsURLs = thumbnailsURLs ? Array.from(thumbnailsURLs) : null;
		await this.basket.dispatchEvent('basket-update', {
			subType: 'product-thumbnailsURLs',
			details: {
				product: this
			}
		});
	};

	async setConfigurationCode(configurationCode: string | null) {
		this.configurationCode = configurationCode;
		await this.basket.dispatchEvent('basket-update', {
			subType: 'product-configurationCode',
			details: {
				product: this
			}
		});
	};

	async setConfigurationReplayURL(configurationReplayURL: string | null) {
		this.configurationReplayURL = configurationReplayURL;
		await this.basket.dispatchEvent('basket-update', {
			subType: 'product-configurationReplayURL',
			details: {
				product: this
			}
		});
	};

	async setConfiguratorReplayURL(configuratorReplayURL: string | null) {
		this.configuratorReplayURL = configuratorReplayURL;
		await this.basket.dispatchEvent('basket-update', {
			subType: 'product-configuratorReplayURL',
			details: {
				product: this
			}
		});
	};

	async setPDFURL(PDFURL: string | null) {
		this.PDFURL = PDFURL;
		await this.basket.dispatchEvent('basket-update', {
			subType: 'product-PDFURL',
			details: {
				product: this
			}
		});
	};

	createPart(desc: Omit<TPart.IDescriptor, 'parent'>) {
		const internalDescriptor = Object.assign({}, desc, {
			parent: this
		});
		if (Object.getOwnPropertyDescriptor(desc, 'title')?.get) {
			Object.defineProperty(internalDescriptor, 'title', {
				get() {
					return desc.title;
				}
			});
		}
		return new window.BasketNS.Part(internalDescriptor);
	};

	hasPart(partsPath: string[]) {
		const [rootPartName, ...remainingPath] = [...partsPath];
		const rootPart = this.parts.find(x => x.id === rootPartName);
		if (!rootPart) {
			return false;
		}
		if (remainingPath.length === 0) {
			return true;
		}
		return (rootPart.hasPart(remainingPath));
	};

	getPart(partsPath: string[]) {
		const [rootPartName, ...remainingPath] = [...partsPath];
		const rootPart = this.parts.find(x => x.id === rootPartName);
		if (!rootPart) {
			throw STD.errors.user(
				`Unknown part<${partsPath.join('.')}>!`,
				{
					rootPart,
					partsPath
				}
			);
		}
		if (remainingPath.length === 0) {
			return (rootPart);
		}
		return (rootPart.getPart(remainingPath));
	};

	getPartCurrentOptionMaybe(partsPath: string[]) {
		if (!this.hasPart(partsPath)) {
			return;
		}
		const part = this.getPart(partsPath);
		return (part.currentOption);
	};

	async serialize(): Promise<Product.ISerialized> {
		return {
			name: this.id,
			title: this.title,
			reference: this.reference?.serialize() || null,
			sku: this.sku?.serialize() || null,
			gtin: this.gtin?.serialize() || null,
			quantity: this.quantity,
			metaData: this.metaData ? <any>Object.assign({}, this.metaData) : null,
			parts: await Promise.all(this.parts.filter(x => !x.ignoreOnSave).map(async x => await x.serialize())),
			unitPrice: this.unitPrice.serialize(),
			totalPrice: this.totalPrice.serialize(),
			thumbnailsURLs: this.thumbnailsURLs,
			configurationCode: this.configurationCode,
			configurationReplayURL: this.configurationReplayURL,
			configuratorReplayURL: this.configuratorReplayURL,
			PDFURL: this.PDFURL
		};
	};

	/* Taxes are not correctly implemented! */
	async computePrice() {
		const unitPrice = window.BasketNS.Price.ofZero();
		for (const part of this.parts) {
			unitPrice.add(await part.computePrice());
		}
		if (this.pricing) {
			unitPrice.add(await this.pricing.resolve());
		}
		this.unitPrice = unitPrice;
		this.totalPrice = new window.BasketNS.Price(
			unitPrice.exclTax.multipliedBy(this.quantity),
			unitPrice.inclTax.multipliedBy(this.quantity),
			unitPrice.taxes
		);
		return (this.totalPrice);
	};

	async initialize() {
		if (this.isInitialized) {
			console.warn('Already initialized product!');
			return;
		}
		for (const part of this.parts) {
			await part.initialize();
		}
		this.isInitialized = true;
		await this.basket.dispatchEvent('basket-update', {
			subType: 'productAdded',
			details: {
				product: this
			}
		});
	};

	constructor(settings: Product.IDescriptor) {
		this.basket = settings.basket;
		this.id = settings.id;
		this.title = settings.title;
		this.viewerCode = settings.viewerCode;
		this.configurationCode = settings.configurationCode;
		this.setReference(settings.reference);
		this.setSKU(settings.sku);
		this.setGTIN(settings.gtin);
		this.quantity = settings.quantity || 1;
		if (settings.metaData) {
			this.metaData = settings.metaData;
		}
		if (settings.pricing) {
			this.pricing = settings.pricing || null;
		}
		for (const partDesc of settings.parts) {
			this.createPart(partDesc);
		}
	};
};
namespace Product {

	export interface IDescriptor {
		readonly basket: TBasket;
		readonly id: string;
		readonly title: string;
		readonly reference: string | TReference;
		readonly viewerCode: string;
		readonly sku: string | TReference;
		readonly gtin: string | TReference | null;
		readonly quantity: number;
		readonly metaData: STD.object.JSONComplexRecursive | null;
		readonly parts: Omit<TPart.IDescriptor, 'parent'>[];
		readonly pricing?: TPricing.TPricing | null;
		readonly configurationCode: string | null;
	};

	export interface ISerialized {
		readonly name: string;
		readonly title: string;
		readonly reference: TReference.ISerialized | null;
		readonly sku: TReference.ISerialized | null;
		readonly gtin: TReference.ISerialized | null;
		readonly quantity: number;
		readonly metaData: Record<any, string> | null;
		readonly parts: TPart.ISerialized[];
		readonly unitPrice: TPrice.ISerialized;
		readonly totalPrice: TPrice.ISerialized;
		readonly thumbnailsURLs: string[] | null;
		readonly configurationCode: string | null;
		readonly configurationReplayURL: string | null;
		readonly configuratorReplayURL: string | null;
		readonly PDFURL: string | null;
	};
}
export default Product;
